import React, { useCallback, useEffect, useState } from 'react';
import { metricsService, MetricsSummary, MinuteBucket } from '../services/metricsService';

const MetricsPage: React.FC = () => {
  const [data, setData] = useState<MetricsSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const d = await metricsService.getSummary();
      setData(d);
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.message || e?.message || 'Failed to load metrics');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(refresh, 5000);
    return () => clearInterval(t);
  }, [autoRefresh, refresh]);

  if (loading) {
    return <div className="p-6 text-sm text-[var(--color-text-secondary)]">Loading metrics…</div>;
  }
  if (error) {
    return (
      <div className="p-6">
        <div
          className="p-4 rounded-md text-sm"
          style={{ backgroundColor: 'var(--color-status-error-light, #fef2f2)', color: 'var(--color-error)' }}
        >
          {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Metrics</h1>
          <p className="text-sm text-[var(--color-text-secondary)] mt-1">
            {data.scope === 'system'
              ? 'System-wide performance (org 1 admin view)'
              : `Performance for org #${data.organization_id}`}{' '}
            · last refreshed {new Date(data.generated_at).toLocaleTimeString()}
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-[var(--color-text-secondary)]">
          <input
            type="checkbox"
            checked={autoRefresh}
            onChange={(e) => setAutoRefresh(e.target.checked)}
            data-testid="metrics-auto-refresh"
          />
          Auto-refresh (5s)
        </label>
      </div>

      {/* Quick KPIs */}
      <Section title="Performance">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Requests / min" value={data.requests.last_minute.count} />
          <Kpi
            label="Error rate (1m)"
            value={`${(data.requests.last_minute.error_rate * 100).toFixed(2)}%`}
            tone={data.requests.last_minute.error_rate > 0.05 ? 'error' : 'success'}
          />
          <Kpi label="p95 latency (1m)" value={`${data.requests.last_minute.p95_ms} ms`} />
          <Kpi label="p99 latency (1m)" value={`${data.requests.last_minute.p99_ms} ms`} />
        </div>

        <div
          className="mt-4 rounded-lg border p-4"
          style={{
            borderColor: 'var(--color-border-default)',
            backgroundColor: 'var(--color-background-card)',
          }}
        >
          <div className="text-sm font-medium text-[var(--color-text-primary)] mb-2">
            Requests per minute (last 10 min)
          </div>
          <Sparkline buckets={data.requests.per_minute} />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-3">
          <WindowStats label="Last 1 min" stats={data.requests.last_minute} />
          <WindowStats label="Last 5 min" stats={data.requests.last_5min} />
          <WindowStats label="Last 10 min" stats={data.requests.last_10min} />
        </div>
      </Section>

      <Section title="System">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Uptime" value={formatUptime(data.uptime_seconds)} />
          <Kpi label="Heap used" value={`${data.process.memory_heap_used_mb} MB`} />
          <Kpi label="RSS" value={`${data.process.memory_rss_mb} MB`} />
          <Kpi
            label="Event-loop lag"
            value={`${data.process.event_loop_lag_ms} ms`}
            tone={data.process.event_loop_lag_ms > 100 ? 'warning' : 'success'}
          />
        </div>

        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card title="Database">
            <Row label="Healthy">
              <Badge ok={data.database.healthy} />
            </Row>
            <Row label="Latency">
              <span>{data.database.latency_ms ?? '—'} ms</span>
            </Row>
            <Row label="Pool total">{data.database.pool_total}</Row>
            <Row label="Pool idle">{data.database.pool_idle}</Row>
            <Row label="Pool waiting">{data.database.pool_waiting}</Row>
          </Card>

          <Card title="Process">
            <Row label="Node">{data.process.node_version}</Row>
            <Row label="PID">{data.process.pid}</Row>
            <Row label="Heap total">{data.process.memory_heap_total_mb} MB</Row>
          </Card>
        </div>
      </Section>

      <Section title="Top routes (last 10 min)">
        {data.requests.top_routes.length === 0 ? (
          <div
            className="p-6 rounded-lg border text-sm text-center text-[var(--color-text-secondary)]"
            style={{
              borderColor: 'var(--color-border-default)',
              backgroundColor: 'var(--color-background-card)',
            }}
          >
            No traffic yet.
          </div>
        ) : (
          <div
            className="rounded-lg border overflow-hidden"
            style={{
              borderColor: 'var(--color-border-default)',
              backgroundColor: 'var(--color-background-card)',
            }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr
                  className="text-left text-xs uppercase text-[var(--color-text-muted)]"
                  style={{ backgroundColor: 'var(--color-background-surface)' }}
                >
                  <th className="px-4 py-2 font-semibold">Route</th>
                  <th className="px-4 py-2 font-semibold text-right">Count</th>
                  <th className="px-4 py-2 font-semibold text-right">p95 ms</th>
                  <th className="px-4 py-2 font-semibold text-right">Error rate</th>
                </tr>
              </thead>
              <tbody>
                {data.requests.top_routes.map((r) => (
                  <tr key={r.route} className="border-t" style={{ borderColor: 'var(--color-border-light)' }}>
                    <td className="px-4 py-2 font-mono text-xs text-[var(--color-text-primary)]">{r.route}</td>
                    <td className="px-4 py-2 text-right">{r.count}</td>
                    <td className="px-4 py-2 text-right">{r.p95_ms}</td>
                    <td
                      className="px-4 py-2 text-right"
                      style={{ color: r.error_rate > 0 ? 'var(--color-error)' : 'inherit' }}
                    >
                      {(r.error_rate * 100).toFixed(2)}%
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>

      <Section title="Org activity">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi label="Users" value={data.org_counters.users_total} />
          <Kpi label="Unread notifications" value={data.org_counters.notifications_unread} />
        </div>
        <div className="mt-4">
          <Card title="Events (last hour)">
            {Object.keys(data.events_last_hour).length === 0 ? (
              <div className="text-sm text-[var(--color-text-secondary)]">No events recorded.</div>
            ) : (
              <ul className="divide-y" style={{ borderColor: 'var(--color-border-light)' }}>
                {Object.entries(data.events_last_hour)
                  .sort((a, b) => b[1] - a[1])
                  .map(([name, count]) => (
                    <li key={name} className="flex items-center justify-between py-2 text-sm">
                      <span className="font-mono text-xs text-[var(--color-text-primary)]">{name}</span>
                      <span className="font-semibold text-[var(--color-text-primary)]">{count}</span>
                    </li>
                  ))}
              </ul>
            )}
          </Card>
        </div>
      </Section>
    </div>
  );
};

/* ---------- helpers / subcomponents ---------- */

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <section>
    <h2 className="text-sm font-semibold uppercase tracking-wider text-[var(--color-text-muted)] mb-3">
      {title}
    </h2>
    {children}
  </section>
);

const Kpi: React.FC<{ label: string; value: React.ReactNode; tone?: 'success' | 'warning' | 'error' }> = ({
  label,
  value,
  tone,
}) => {
  const color =
    tone === 'error'
      ? 'var(--color-error)'
      : tone === 'warning'
      ? 'var(--color-warning)'
      : tone === 'success'
      ? 'var(--color-success)'
      : 'var(--color-text-primary)';
  return (
    <div
      className="rounded-lg border p-4"
      style={{
        borderColor: 'var(--color-border-default)',
        backgroundColor: 'var(--color-background-card)',
      }}
    >
      <div className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] mb-1">
        {label}
      </div>
      <div className="text-2xl font-bold" style={{ color }}>
        {value}
      </div>
    </div>
  );
};

const Card: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <div
    className="rounded-lg border p-4"
    style={{
      borderColor: 'var(--color-border-default)',
      backgroundColor: 'var(--color-background-card)',
    }}
  >
    <div className="text-sm font-semibold text-[var(--color-text-primary)] mb-2">{title}</div>
    {children}
  </div>
);

const Row: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-center justify-between py-1 text-sm">
    <span className="text-[var(--color-text-secondary)]">{label}</span>
    <span className="text-[var(--color-text-primary)]">{children}</span>
  </div>
);

const Badge: React.FC<{ ok: boolean }> = ({ ok }) => (
  <span
    className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase"
    style={{
      backgroundColor: ok ? 'var(--color-success)' + '22' : 'var(--color-error)' + '22',
      color: ok ? 'var(--color-success)' : 'var(--color-error)',
    }}
  >
    {ok ? 'healthy' : 'down'}
  </span>
);

const WindowStats: React.FC<{
  label: string;
  stats: { count: number; error_count: number; error_rate: number; p50_ms: number; p95_ms: number; p99_ms: number };
}> = ({ label, stats }) => (
  <div
    className="rounded-lg border p-3"
    style={{
      borderColor: 'var(--color-border-default)',
      backgroundColor: 'var(--color-background-card)',
    }}
  >
    <div className="text-xs uppercase tracking-wider text-[var(--color-text-muted)] mb-2">{label}</div>
    <div className="grid grid-cols-2 gap-2 text-sm">
      <Row label="Requests">{stats.count}</Row>
      <Row label="Errors">{stats.error_count}</Row>
      <Row label="p50">{stats.p50_ms} ms</Row>
      <Row label="p95">{stats.p95_ms} ms</Row>
      <Row label="p99">{stats.p99_ms} ms</Row>
      <Row label="Err %">{(stats.error_rate * 100).toFixed(2)}%</Row>
    </div>
  </div>
);

const Sparkline: React.FC<{ buckets: MinuteBucket[] }> = ({ buckets }) => {
  if (buckets.length === 0) return null;
  const max = Math.max(1, ...buckets.map((b) => b.count));
  const w = 100;
  const h = 40;
  const step = w / Math.max(1, buckets.length - 1);

  const linePoints = buckets
    .map((b, i) => `${(i * step).toFixed(2)},${(h - (b.count / max) * (h - 4) - 2).toFixed(2)}`)
    .join(' ');

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" className="w-full h-16">
        <polyline
          fill="none"
          stroke="var(--color-primary)"
          strokeWidth="1.5"
          points={linePoints}
          vectorEffect="non-scaling-stroke"
        />
        {buckets.map((b, i) => (
          <circle
            key={i}
            cx={(i * step).toFixed(2)}
            cy={(h - (b.count / max) * (h - 4) - 2).toFixed(2)}
            r={b.errors > 0 ? 1.4 : 1}
            fill={b.errors > 0 ? 'var(--color-error)' : 'var(--color-primary)'}
          />
        ))}
      </svg>
      <div className="flex items-center justify-between text-xs text-[var(--color-text-muted)] mt-1">
        <span>10m ago</span>
        <span>peak {max} req/min</span>
        <span>now</span>
      </div>
    </div>
  );
};

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const m = Math.floor(seconds / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  const remM = m % 60;
  if (h < 24) return `${h}h ${remM}m`;
  const d = Math.floor(h / 24);
  return `${d}d ${h % 24}h`;
}

export default MetricsPage;
